import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import ChatInterface from '../components/copilot/ChatInterface';
import { Bot, Sparkles, MessageSquare, Brain, Zap, Shield, FileText, GitCompareArrows } from 'lucide-react';

const translations: Record<string, Record<string, string>> = {
    en: {
        title: 'Aipron CoPilot™',
        subtitle: 'Your AI-powered operations assistant. Ask questions, get insights, and make better decisions.',
        cta: 'Start Conversation',
        feat_queries: 'Natural Language Queries',
        feat_queries_desc: 'Ask about flights, delays, capacity, and operations in plain language.',
        feat_rag: 'Knowledge Base Access',
        feat_rag_desc: 'Retrieval-Augmented Generation (RAG) with ICAO/IATA manuals and airport topology.',
        feat_insights: 'Real-time Insights',
        feat_insights_desc: 'Instant analysis of current operational status with actionable recommendations.',
        feat_secure: 'Secure & Private',
        feat_secure_desc: 'In-flight PII anonymization and enterprise-grade data protection.',
        feat_narrative: 'Report Narratives',
        feat_narrative_desc: 'Auto-generate executive, technical, or operational reports from analysis results.',
        feat_compare: 'Scenario Comparison',
        feat_compare_desc: 'Compare KPIs between projects or scenarios with delta analysis.',
        powered: 'Powered by',
        try_label: 'Try asking:',
        try1: '"Genera un informe ejecutivo"',
        try2: '"Compare PTY vs MAD capacity"',
        try3: '"Explain why BCI is low"',
    },
    es: {
        title: 'Aipron CoPilot™',
        subtitle: 'Tu asistente de operaciones con IA. Haz preguntas, obtén insights y toma mejores decisiones.',
        cta: 'Iniciar Conversación',
        feat_queries: 'Consultas en Lenguaje Natural',
        feat_queries_desc: 'Pregunta sobre vuelos, retrasos, capacidad y operaciones en lenguaje natural.',
        feat_rag: 'Base de Conocimiento',
        feat_rag_desc: 'Generación con RAG sobre manuales ICAO/IATA y topología del aeropuerto.',
        feat_insights: 'Insights en Tiempo Real',
        feat_insights_desc: 'Análisis instantáneo del estado operacional con recomendaciones accionables.',
        feat_secure: 'Seguro y Privado',
        feat_secure_desc: 'Anonimización PII en vuelo y protección de datos de nivel empresarial.',
        feat_narrative: 'Narrativas para Informes',
        feat_narrative_desc: 'Genera automáticamente informes ejecutivos, técnicos u operacionales.',
        feat_compare: 'Comparación de Escenarios',
        feat_compare_desc: 'Compara KPIs entre proyectos o escenarios con análisis de deltas.',
        powered: 'Impulsado por',
        try_label: 'Prueba preguntar:',
        try1: '"Genera un informe ejecutivo"',
        try2: '"Compara la capacidad de PTY vs MAD"',
        try3: '"Explica por qué el BCI es bajo"',
    },
};

const CoPilotPage: React.FC = () => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';
    const [isChatOpen, setIsChatOpen] = useState(false);
    const t = translations[language] || translations.en;

    // Auto-open chat on mount
    useEffect(() => {
        const timer = setTimeout(() => setIsChatOpen(true), 800);
        return () => clearTimeout(timer);
    }, []);

    const features = [
        { icon: MessageSquare, title: t.feat_queries, description: t.feat_queries_desc, color: 'text-blue-400', bg: 'from-blue-500/20 to-cyan-500/20' },
        { icon: Brain, title: t.feat_rag, description: t.feat_rag_desc, color: 'text-purple-400', bg: 'from-purple-500/20 to-violet-500/20' },
        { icon: Zap, title: t.feat_insights, description: t.feat_insights_desc, color: 'text-amber-400', bg: 'from-amber-500/20 to-orange-500/20' },
        { icon: Shield, title: t.feat_secure, description: t.feat_secure_desc, color: 'text-emerald-400', bg: 'from-emerald-500/20 to-teal-500/20' },
        { icon: FileText, title: t.feat_narrative, description: t.feat_narrative_desc, color: 'text-pink-400', bg: 'from-pink-500/20 to-rose-500/20' },
        { icon: GitCompareArrows, title: t.feat_compare, description: t.feat_compare_desc, color: 'text-cyan-400', bg: 'from-cyan-500/20 to-sky-500/20' },
    ];

    return (
        <div className={`min-h-screen p-8 ${'bg-[var(--bg)]'}`}>
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className={`
                        inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6
                        bg-gradient-to-br from-pink-500/20 to-purple-500/20
                        border border-pink-500/30
                    `}>
                        <Bot className="w-10 h-10 text-pink-400" />
                    </div>

                    <h1 className={`text-4xl font-bold mb-4 ${'text-[var(--text-primary)]'}`}>
                        {t.title}
                    </h1>
                    <p className={`text-lg max-w-2xl mx-auto ${'text-[var(--text-secondary)]'}`}>
                        {t.subtitle}
                    </p>

                    {/* CTA Button */}
                    <button
                        onClick={() => setIsChatOpen(true)}
                        className={`
                            mt-8 inline-flex items-center gap-3 px-8 py-4
                            rounded-xl text-base font-semibold
                            bg-gradient-to-r from-pink-500 to-purple-500
                            text-white shadow-lg shadow-pink-500/25
                            hover:from-pink-600 hover:to-purple-600
                            hover:shadow-xl hover:shadow-pink-500/30
                            transition-all duration-300 group
                        `}
                    >
                        <Sparkles className="w-5 h-5 group-hover:animate-pulse" />
                        <span>{t.cta}</span>
                    </button>
                </div>

                {/* Try Asking */}
                <div className={`
                    text-center mb-8 p-4 rounded-xl border
                    ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)]' : 'bg-white border-[var(--border)]'}
                `}>
                    <p className={`text-sm font-medium mb-3 ${'text-[var(--text-secondary)]'}`}>
                        {t.try_label}
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        {[t.try1, t.try2, t.try3].map((q, i) => (
                            <button
                                key={i}
                                onClick={() => setIsChatOpen(true)}
                                className={`
                                    px-4 py-2 rounded-lg text-sm font-mono
                                    transition-all duration-200 cursor-pointer
                                    ${isDark
                                        ? 'bg-pink-500/10 text-pink-300 border border-pink-500/20 hover:bg-pink-500/20'
                                        : 'bg-pink-50 text-pink-700 border border-pink-200 hover:bg-pink-100'}
                                `}
                            >
                                {q}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className={`
                                p-6 rounded-xl border backdrop-blur-xl
                                transition-all duration-300 hover:scale-[1.02]
                                ${isDark
                                    ? 'bg-[var(--surface-raised)] border-[var(--border)] hover:border-white/20'
                                    : 'bg-white border-[var(--border)] hover:shadow-md'}
                            `}
                        >
                            <div className={`
                                w-12 h-12 rounded-lg mb-4 flex items-center justify-center
                                bg-gradient-to-br ${feature.bg}
                            `}>
                                <feature.icon className={`w-6 h-6 ${feature.color}`} />
                            </div>
                            <h3 className={`text-lg font-semibold mb-2 ${'text-[var(--text-primary)]'}`}>
                                {feature.title}
                            </h3>
                            <p className={`text-sm leading-relaxed ${'text-[var(--text-secondary)]'}`}>
                                {feature.description}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Tech Stack */}
                <div className={`
                    mt-10 p-5 rounded-xl border text-center
                    ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)]' : 'bg-white border-[var(--border)]'}
                `}>
                    <p className={`text-xs uppercase tracking-wider mb-2 ${isDark ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]'}`}>
                        {t.powered}
                    </p>
                    <p className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Gemini 2.0 Flash • LangChain • LangGraph • DACAMS Knowledge Base
                    </p>
                </div>
            </div>

            {/* Chat Interface */}
            <ChatInterface
                projectId=""
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
            />
        </div>
    );
};

export default CoPilotPage;
